/** @jsxImportSource @emotion/react */
import { useSelector } from "react-redux";
import * as authSelectors from "../../auth/authSelectors";
import { useFieldArray, useFormContext } from "react-hook-form";
import { FormRow } from "../../common/hook-form";
import * as Inputs from "../../common/hook-form";
import { StyledText } from "../../../assets/typography/typography";
import { colors } from "../../../components/colors";
import { css } from "@emotion/react";
import { SecondaryButton } from "../../../components/Buttons";
import {
  useCreateDeliveryRuleGroup,
  useUpdateDeliveryRuleGroup,
} from "../../../service/api";
import { DeliveryRuleForm } from "../../../service/api-response/DeliveryRule";
import { DeliveryRulesFooter } from "./DeliveryRulesFooter";
import { DeliveryRules } from "./DeliveryRules";
import { toastr } from "react-redux-toastr";
import {
  PriceInput,
  transformPriceInput,
} from "../../../components/forms/inputs/PriceInput";
import { getErrorMessage } from "../../../error-handler";
import { SelectProductInput } from "../../../components/forms/inputs/SelectProductInput";

type Props = {
  isEdit: boolean;
  ruleId?: string;
  onClose: () => void;
};

const priceInputAdditionalStyling = css`
  margin-bottom: 16px;
`;

const bodyWrapperStyle = css`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 650px;
  min-height: 450px;
  overflow: scroll;
  padding-bottom: 80px;
`;

const subHeaderStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;
  margin-bottom: 32px;
`;

export const DeliveryRulesModalBody = ({ isEdit, ruleId, onClose }: Props) => {
  const organization = useSelector(authSelectors.selectedOrganizationKeyName);

  const methods = useFormContext<DeliveryRuleForm>();
  const {
    register,
    formState: { errors: formErrors },
    reset,
    control,
  } = methods;

  const {
    mutateAsync: createDeliveryRuleGroup,
    isLoading: creationIsLoading,
  } = useCreateDeliveryRuleGroup();

  const {
    mutateAsync: updateDeliveryRuleGroup,
    isLoading: updateIsLoading,
  } = useUpdateDeliveryRuleGroup(ruleId);

  const onCreate = async (data: DeliveryRuleForm) => {
    try {
      await createDeliveryRuleGroup(data);
      toastr.success("Success", "Delivery rule group created succesfully");
      reset();
      onClose();
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  const onUpdate = async (data: DeliveryRuleForm) => {
    try {
      await updateDeliveryRuleGroup(data);
      toastr.success("Success", "Delivery rule group updated succesfully");
      reset();
      onClose();
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  const { fields, append, remove } = useFieldArray({
    control: control,
    name: "rules",
  });

  const onSubmit = async (data: DeliveryRuleForm) => {
    const { minimumPriceAmount, ...rest } = data;
    if (isEdit) {
      const request = {
        organizationKeyName: organization,
        id: ruleId,
        minimumPriceAmount: transformPriceInput(
          minimumPriceAmount,
          rest.minimumPriceCurrency
        ),
        ...rest,
      };
      await onUpdate(request);
      return;
    }
    const request = {
      organizationKeyName: organization,
      minimumPriceAmount: transformPriceInput(
        minimumPriceAmount,
        rest.minimumPriceCurrency
      ),
      ...rest,
    };
    await onCreate(request);
  };

  return (
    <div css={bodyWrapperStyle}>
      {!isEdit && (
        <>
          <FormRow>
            <Inputs.InputFormField
              errors={formErrors}
              register={register}
              label="Delivery rule group name"
              name="name"
              type="text"
              helpText="Choose name for delivery rule."
            />
          </FormRow>
          <FormRow>
            <SelectProductInput
              name="bufferProductId"
              classification="delivery_buffer"
              label="Delivery extra fee product"
              helperText="Select delivery extra fee product which will be added to a user's order if minimum purchase price is not reached."
            />
          </FormRow>
        </>
      )}
      <FormRow css={priceInputAdditionalStyling}>
        <PriceInput
          formMethods={methods}
          name="minimumPriceAmount"
          currencyName="minimumPriceCurrency"
          label="Minimum price"
          helpertext="Choose the minimum price that users has to pay when making delivery orders. Value in kr. - e.g. 100 is 100 kr."
        />
        <Inputs.SelectCurrencyFormField
          errors={methods.formState.errors}
          register={register}
          label="Currency"
          name="minimumPriceCurrency"
        />
      </FormRow>
      <div css={subHeaderStyle}>
        <StyledText
          className="bodyHeaderOneSemi mb-0"
          color={colors.textPrimary}
        >
          Rules
        </StyledText>
        <StyledText
          className="bodyTextRegular mb-0"
          color={colors.textSecondary}
        >
          Add rules to this delivery rule group. Rules include a minimum and
          maximum distance along with the delivery product
        </StyledText>
      </div>
      {fields.length === 0 && (
        <SecondaryButton
          color={colors.textPrimary}
          backgroundColor={colors.transparent}
          hoverColor={colors.ctaLight}
          type="button"
          onClick={() =>
            append({
              minimumDistance: 0,
              maximumDistance: 0,
              productId: "",
            })
          }
        >
          + Add rule
        </SecondaryButton>
      )}
      <DeliveryRules fields={fields} remove={remove} />
      <DeliveryRulesFooter
        isEdit={isEdit}
        isLoading={creationIsLoading || updateIsLoading}
        onSubmit={onSubmit}
        append={append}
        fields={fields}
      />
    </div>
  );
};
